import { type IMergedSlot, PU_EVENT_TYPE, type PuEventType } from '../interface/pu-events';

// Pure duration/interval logic for the Election Day Pulse timeline — no React.
// All timestamps are the ISO 8601 `occurredAt` tap-time strings (authoritative
// witness clock). A slot counts as "captured" here when its status is
// 'captured' OR 'syncing' (optimistic local capture awaiting the server).

const isCaptured = (slot: IMergedSlot): boolean => slot.status === 'captured' || slot.status === 'syncing';

const parseIso = (iso: string | null): number | null => {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	return Number.isFinite(t) ? t : null;
};

/** Milliseconds between two ISO timestamps; null when either is missing/invalid. */
export const durationMs = (fromIso: string | null, toIso: string | null): number | null => {
	const from = parseIso(fromIso);
	const to = parseIso(toIso);
	if (from === null || to === null) return null;
	return to - from;
};

/**
 * Human duration label from a millisecond magnitude (sign is ignored —
 * callers carry direction separately): <60s → '<1 min'; <60min → 'N min';
 * otherwise 'Hh Mm' ('6h 2m'), collapsing exact hours to '6h'.
 */
export const formatDuration = (ms: number): string => {
	const totalMinutes = Math.round(Math.abs(ms) / 60_000);
	if (totalMinutes < 1) return '<1 min';
	if (totalMinutes < 60) return `${totalMinutes} min`;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

/** Relative "32 min ago" / "1h 5m ago" label; future timestamps clamp to 'just now'. */
export const formatAgo = (iso: string | null, now: Date): string | null => {
	const then = parseIso(iso);
	if (then === null) return null;
	const ms = now.getTime() - then;
	if (ms < 60_000) return 'just now';
	return `${formatDuration(ms)} ago`;
};

export interface IIntervalDelta {
	/** Signed milliseconds from the previous captured slot; negative = out of order. */
	ms: number;
	/** Formatted absolute magnitude ('11 min', '1h 5m'). */
	label: string;
	outOfOrder: boolean;
	/** True when uncaptured slots sit between the two anchors in display order. */
	spansGap: boolean;
	fromEventType: PuEventType;
	toEventType: PuEventType;
}

/**
 * The interval to show on the rail connector ABOVE each captured/syncing slot,
 * anchored on the previous captured/syncing slot in display order (skipping
 * pending gaps — out-of-order capture is allowed, election day is messy).
 * Keyed by the lower slot's eventType; the first captured slot gets no entry.
 */
export const deriveIntervalDeltas = (slots: IMergedSlot[]): Map<PuEventType, IIntervalDelta> => {
	const deltas = new Map<PuEventType, IIntervalDelta>();
	let anchor: IMergedSlot | null = null;
	let gapSinceAnchor = false;
	for (const slot of slots) {
		if (!isCaptured(slot) || !parseIso(slot.occurredAt)) {
			if (anchor && slot.status === 'pending') gapSinceAnchor = true;
			continue;
		}
		if (anchor) {
			const ms = durationMs(anchor.occurredAt, slot.occurredAt);
			if (ms !== null) {
				deltas.set(slot.eventType, {
					ms,
					label: formatDuration(ms),
					outOfOrder: ms < 0,
					spansGap: gapSinceAnchor,
					fromEventType: anchor.eventType,
					toEventType: slot.eventType,
				});
			}
		}
		anchor = slot;
		gapSinceAnchor = false;
	}
	return deltas;
};

export interface IScheduleVariance {
	/** Signed minutes vs the expected wall-clock time: positive = late. */
	deltaMin: number;
	label: string;
	tone: 'ontime' | 'late' | 'early';
}

const minutesOfDay = (date: Date): number => date.getHours() * 60 + date.getMinutes();

/**
 * How a captured event compares to its INEC expected wall-clock time
 * ('On time' within ±tolerance, '+45 min late', '20 min early'). Same-day
 * minutes-of-day diff in device-local time — election events are same-day by
 * design. null when there is no expected time (sequence-derived events) or no
 * capture timestamp.
 */
export const deriveScheduleVariance = (
	occurredAt: string | null,
	expectedHHMM: string | null,
	onTimeToleranceMin = 10
): IScheduleVariance | null => {
	if (!expectedHHMM) return null;
	const occurred = parseIso(occurredAt);
	if (occurred === null) return null;
	const [h, m] = expectedHHMM.split(':').map(Number);
	const deltaMin = minutesOfDay(new Date(occurred)) - (h * 60 + (m || 0));
	if (Math.abs(deltaMin) <= onTimeToleranceMin) {
		return { deltaMin, label: 'On time', tone: 'ontime' };
	}
	const magnitude = formatDuration(Math.abs(deltaMin) * 60_000);
	return deltaMin > 0
		? { deltaMin, label: `+${magnitude} late`, tone: 'late' }
		: { deltaMin, label: `${magnitude} early`, tone: 'early' };
};

export interface ICompletionSummary {
	/** OfficialArrived → ResultsAnnounced. */
	totalSpanMs: number | null;
	/** VotingCommenced → VotingStopped. */
	votingMs: number | null;
	/** CountingCommenced → CountingStopped. */
	countingMs: number | null;
}

/** Day-summary durations from the merged slots; each stat is null when an anchor is missing. */
export const deriveCompletionSummary = (slots: IMergedSlot[]): ICompletionSummary => {
	const occurredOf = (eventType: PuEventType): string | null => {
		const slot = slots.find((s) => s.eventType === eventType);
		return slot && isCaptured(slot) ? slot.occurredAt : null;
	};
	return {
		totalSpanMs: durationMs(occurredOf(PU_EVENT_TYPE.OfficialArrived), occurredOf(PU_EVENT_TYPE.ResultsAnnounced)),
		votingMs: durationMs(occurredOf(PU_EVENT_TYPE.VotingCommenced), occurredOf(PU_EVENT_TYPE.VotingStopped)),
		countingMs: durationMs(occurredOf(PU_EVENT_TYPE.CountingCommenced), occurredOf(PU_EVENT_TYPE.CountingStopped)),
	};
};
